import React, { useState, useEffect } from 'react';
import { 
  Image, Calendar, ThumbsUp, Edit3, X, Loader2, ChevronRight,
  Instagram, Linkedin, Twitter, Youtube, Play, FileText,
  CheckCircle2, Clock, AlertTriangle, Eye, MessageSquare
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const PLATFORMS = {
  instagram: { icon: Instagram, color: 'bg-pink-500/20 text-pink-400', label: 'Instagram' },
  linkedin: { icon: Linkedin, color: 'bg-blue-500/20 text-blue-400', label: 'LinkedIn' },
  twitter: { icon: Twitter, color: 'bg-cyan-500/20 text-cyan-400', label: 'Twitter' },
  youtube: { icon: Youtube, color: 'bg-red-500/20 text-red-400', label: 'YouTube' },
  tiktok: { icon: Play, color: 'bg-purple-500/20 text-purple-400', label: 'TikTok' }
};

const SERIES = {
  always_elevating: { label: 'Always Elevating', color: 'bg-[#C4A052]/20 text-[#C4A052]' },
  film_room: { label: 'Film Room', color: 'bg-green-500/20 text-green-400' },
  beyond_the_game: { label: 'Beyond the Game', color: 'bg-blue-500/20 text-blue-400' },
  community_first: { label: 'Community First', color: 'bg-purple-500/20 text-purple-400' }
};

const PILLARS = {
  performance: { label: 'Performance', color: 'text-green-400' },
  lifestyle: { label: 'Lifestyle', color: 'text-blue-400' },
  purpose: { label: 'Purpose', color: 'text-purple-400' },
  personal: { label: 'Personal', color: 'text-pink-400' }
};

const STATUSES = {
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400' },
  internal_review: { label: 'Internal Review', color: 'bg-yellow-500/20 text-yellow-400' },
  ready_for_client: { label: 'Ready for Review', color: 'bg-[#C4A052]/20 text-[#C4A052]' },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-500/20 text-orange-400' },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400' },
  published: { label: 'Published', color: 'bg-emerald-500/20 text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400' }
};

const AEContent = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [seriesFilter, setSeriesFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalAction, setApprovalAction] = useState(null);

  useEffect(() => {
    if (userEmail) {
      fetchContent();
    }
  }, [userEmail]);

  const fetchContent = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/content?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setContent(data.data);
      }
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (action) => {
    if (!selectedContent) return;
    
    setActionLoading(action);
    try {
      const res = await fetch(`${API}/api/always-elevate/content/${selectedContent.id}/approve?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action, 
          comment: approvalComment 
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowApprovalModal(false);
        setApprovalComment('');
        setApprovalAction(null);
        fetchContent();
        // Update selected content status
        setSelectedContent(prev => prev ? { ...prev, status: data.new_status } : null);
      }
    } catch (err) {
      console.error('Error processing approval:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const openApprovalModal = (action) => {
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const filteredContent = content.filter(item => {
    if (filter === 'review') return item.status === 'ready_for_client';
    if (filter === 'approved') return item.status === 'approved' || item.status === 'scheduled' || item.status === 'published';
    if (filter === 'changes') return item.status === 'changes_requested';
    if (seriesFilter !== 'all' && item.series !== seriesFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  const reviewCount = content.filter(c => c.status === 'ready_for_client').length;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>
                Content & Approvals
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {reviewCount > 0 ? `${reviewCount} items awaiting your review` : 'All caught up!'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            {[
              { id: 'all', label: 'All Content' },
              { id: 'review', label: 'Needs Review', count: reviewCount },
              { id: 'approved', label: 'Approved' },
              { id: 'changes', label: 'Changes Requested' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  filter === f.id 
                    ? 'bg-[#C4A052] text-black font-medium' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {f.label}
                {f.count > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-black/30 rounded text-xs">{f.count}</span>
                )}
              </button>
            ))}
          </div>
          
          <div className="h-6 w-px bg-white/10" />
          
          <select
            value={seriesFilter}
            onChange={(e) => setSeriesFilter(e.target.value)}
            className="bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
          >
            <option value="all">All Series</option>
            {Object.entries(SERIES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content List */}
          <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
            {filteredContent.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-8 text-center">
                <Image className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No content found</p>
              </div>
            ) : (
              filteredContent.map(item => {
                const platform = PLATFORMS[item.platform] || PLATFORMS.instagram;
                const PlatformIcon = platform.icon;
                const status = STATUSES[item.status] || STATUSES.draft;
                const series = SERIES[item.series];
                
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedContent(item)}
                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                      selectedContent?.id === item.id ? 'border-[#C4A052]' : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${platform.color}`}>
                        <PlatformIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{item.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                            {status.label}
                          </span>
                          {series && (
                            <span className={`text-xs px-2 py-0.5 rounded ${series.color}`}>
                              {series.label}
                            </span>
                          )}
                        </div>
                        {item.scheduled_date && (
                          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(item.scheduled_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Content Detail */}
          <div className="lg:col-span-2">
            {selectedContent ? (
              <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
                {/* Content Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded ${PLATFORMS[selectedContent.platform]?.color || PLATFORMS.instagram.color}`}>
                          {PLATFORMS[selectedContent.platform]?.label || 'Social'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUSES[selectedContent.status]?.color || STATUSES.draft.color}`}>
                          {STATUSES[selectedContent.status]?.label || selectedContent.status}
                        </span>
                        {selectedContent.series && SERIES[selectedContent.series] && (
                          <span className={`text-xs px-2 py-0.5 rounded ${SERIES[selectedContent.series].color}`}>
                            {SERIES[selectedContent.series].label}
                          </span>
                        )}
                        {selectedContent.pillar && PILLARS[selectedContent.pillar] && (
                          <span className={`text-xs ${PILLARS[selectedContent.pillar].color}`}>
                            {PILLARS[selectedContent.pillar].label}
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-bold">{selectedContent.title}</h2>
                    </div>
                    <button 
                      onClick={() => setSelectedContent(null)}
                      className="p-2 hover:bg-white/10 rounded-lg lg:hidden"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                    {selectedContent.scheduled_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Scheduled: {new Date(selectedContent.scheduled_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedContent.monthly_theme && (
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>Theme: {selectedContent.monthly_theme}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content Body */}
                <div className="p-6 space-y-6">
                  {/* Caption */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Caption</h3>
                    <div className="bg-white/5 rounded-lg p-4 text-sm text-gray-300 whitespace-pre-wrap">
                      {selectedContent.caption || 'No caption provided.'}
                    </div>
                  </div>

                  {/* Media Preview Placeholder */}
                  {selectedContent.media_urls && selectedContent.media_urls.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Media</h3>
                      <div className="bg-white/5 rounded-lg p-8 text-center border border-dashed border-white/20">
                        <Image className="w-12 h-12 mx-auto mb-2 text-gray-500" />
                        <p className="text-sm text-gray-400">{selectedContent.media_urls.length} media file(s) attached</p>
                      </div>
                    </div>
                  )}

                  {/* Approval History */}
                  {selectedContent.approval_history && selectedContent.approval_history.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Approval History</h3>
                      <div className="space-y-2">
                        {selectedContent.approval_history.map((entry, idx) => (
                          <div key={idx} className="bg-white/5 rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between">
                              <span className={`capitalize ${
                                entry.action === 'approve' ? 'text-green-400' :
                                entry.action === 'reject' ? 'text-red-400' :
                                'text-orange-400'
                              }`}>
                                {entry.action?.replace(/_/g, ' ')}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {new Date(entry.timestamp).toLocaleString()}
                              </span>
                            </div>
                            {entry.comment && (
                              <p className="text-gray-400 mt-1">{entry.comment}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {selectedContent.status === 'ready_for_client' && (
                    <div className="border-t border-white/10 pt-6">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Your Decision</h3>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => openApprovalModal('approve')}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => openApprovalModal('request_changes')}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                          Request Changes
                        </button>
                        <button
                          onClick={() => openApprovalModal('reject')}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Status Message */}
                  {selectedContent.status === 'approved' && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <p className="text-green-400">This content has been approved and will be scheduled for posting.</p>
                    </div>
                  )}

                  {selectedContent.status === 'changes_requested' && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <p className="text-orange-400">Changes requested - the team is working on revisions.</p>
                    </div>
                  )}

                  {selectedContent.status === 'scheduled' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <p className="text-blue-400">
                        Scheduled for {selectedContent.scheduled_date ? new Date(selectedContent.scheduled_date).toLocaleDateString() : 'posting'}
                      </p>
                    </div>
                  )}

                  {selectedContent.status === 'published' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <p className="text-emerald-400">This content has been published!</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Select Content</h3>
                <p className="text-gray-500 text-sm">Click on a content item from the list to view details and take action</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">
              {approvalAction === 'approve' && 'Approve Content'}
              {approvalAction === 'request_changes' && 'Request Changes'}
              {approvalAction === 'reject' && 'Reject Content'}
            </h3>
            
            <textarea
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder={
                approvalAction === 'approve' 
                  ? 'Add a comment (optional)...' 
                  : 'Please explain what changes are needed...'
              }
              className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-sm resize-none h-32 focus:outline-none focus:border-[#C4A052] mb-4"
            />
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setApprovalComment('');
                  setApprovalAction(null);
                }}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApproval(approvalAction)}
                disabled={actionLoading || (approvalAction !== 'approve' && !approvalComment.trim())}
                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  approvalAction === 'approve' 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : approvalAction === 'reject'
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  approvalAction === 'approve' ? 'Approve' :
                  approvalAction === 'reject' ? 'Reject' : 'Submit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEContent;
